import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseSection } from 'components/layouts';
import { Row, Col, Divider, Space, Select, Input } from 'antd';
import { BaseContainer, SearchDesc, AlertModal } from 'components/containers';
import { BaseDatePicker, DataGrid, FillButton } from 'components/elements';
import { GridSetting, DownloadExcel, AttachmentButton } from 'pages/common';
import { IconFilePdf } from 'components/assets/icons';
import { getListAPI } from 'api/common';
import { fnSetColumn, utilSearchData } from 'utils/utils';
import { searchTypeOptions, firstOrderStatusOptions, firstOrderApproveStatusOptions } from 'data/options';
import { postOrderFirstAPI, putOrderFirstAPI } from 'api/hq';

const InitialProductOrder = ({ common }) => {
  const navigate = useNavigate();
  const [params, setParams] = useState({
    order_status: -1,
    search_state: 'branch_name',
    search_content: ''
  });
  const [orderState, setOrderState] = useState(-1);
  const [dataList, setDataList] = useState([]);
  const [columns, setColumns] = useState([]);
  const [gridSetting, setGridSetting] = useState([]);
  const [stats, setStats] = useState([]);
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState({ isDetail: false });
  const [alertModal, setAlertModal] = useState({
    open: false,
    message: ''
  });

  const fnCloseAlertModal = () => {
    setAlertModal({ ...alertModal, open: false, message: '' });
  };

  //초도 승인/반려
  const fnChangeStatus = async (record, v) => {
    const param = {
      idx: record.IDX,
      school_idx: record.SCHOOL_IDX,
      ...v
    };

    await postOrderFirstAPI(param)
      .then((res) => {
        if (res?.data?.responseCode === 200) {
          fnGetList();
        }
      })
      .catch((err) => console.log(err));
  };

  // 주문상태 일괄수정
  const fnChangeCheckedStatus = async () => {
    const param = {
      order_idx: rows,
      order_status: orderState
    };
    if (orderState === -1) {
      setAlertModal({ ...alertModal, open: true, message: '주문상태를 선택해주세요.' });
      return;
    }
    if (rows.length < 1) {
      setAlertModal({ ...alertModal, open: true, message: '주문상태를 수정할 목록에서 체크해 주세요' });
      return;
    }
    if (window.confirm('주문 상태를 일괄 수정하시겠습니까?')) {
      await putOrderFirstAPI(param)
        .then((res) => {
          if (res?.data?.responseCode === 200) {
            setRows([]);
            setOrderState(-1);
            fnGetList();
          }
        })
        .catch((err) => console.error(err));
    }
  };

  const fnGetList = async () => {
    try {
      // 공통 서치바 데이터 체크 및 데이터 리턴
      const result = utilSearchData(params);

      if (typeof result === 'string') {
        setAlertModal({ ...alertModal, open: true, message: result });
      } else {
        const { data } = await getListAPI(result);
        setStats(data?.STATICS_LIST);
        setColumns(fnSetColumn(data?.COLUMS_LIST, column));
        setDataList(data?.DATA_LIST);
        setGridSetting(data?.COLUMS_LIST);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fnGetList();
  }, []);

  const items = [
    {
      key: 1,
      label: '주문일',
      children: (
        <>
          <Space>
            <BaseDatePicker value={params?.order_start_date} placeholder="날짜선택" format={'YYYY-MM-DD'} suffixIcon={null} onChange={(e) => setParams({ ...params, order_start_date: e })} />
            ~
            <BaseDatePicker value={params?.order_end_date} placeholder="날짜선택" format={'YYYY-MM-DD'} suffixIcon={null} onChange={(e) => setParams({ ...params, order_end_date: e })} />
          </Space>
        </>
      ),
      span: 1
    },
    {
      key: 2,
      label: '주문상태',
      children: (
        <>
          <Select
            placeholder={'전체'}
            value={params?.order_status}
            style={{ width: '10rem' }}
            options={firstOrderStatusOptions}
            onSelect={(e) => {
              setParams({ ...params, order_status: e });
            }}
          />
        </>
      ),
      span: 1
    },
    {
      key: 3,
      label: '검색조건',
      children: (
        <Space>
          <Select placeholder={'선택'} value={params?.search_state} style={{ width: '10rem' }} options={searchTypeOptions(['교육원명', '지사명'])} onSelect={(e) => setParams({ ...params, search_state: e })} />
          <Input value={params?.search_content} onChange={(e) => setParams({ ...params, search_content: e.target.value })} style={{ width: '12rem' }} />
        </Space>
      ),
      span: 1
    },
    {
      key: 4,
      label: <FillButton label="검색하기" width="100px" backgroundColor="var(--color-gray03)" onClick={fnGetList} />,
      children: <></>
    }
  ];
  const items2 = [
    {
      key: 1,
      label: '주문상태 수정',
      children: (
        <Select
          placeholder={'변경할 상태 선택'}
          value={orderState}
          style={{ width: '20rem' }}
          options={firstOrderStatusOptions}
          onSelect={(e) => {
            setOrderState(e);
          }}
        />
      ),
      span: 1
    },
    {
      key: 2,
      label: <FillButton label="일괄 상태변경" backgroundColor="var(--color-gray03)" onClick={fnChangeCheckedStatus} />,
      children: <></>,
      span: 1
    }
  ];

  const column = [
    {
      title: '주문번호',
      align: 'center',
      dataIndex: 'ORDER_IDX'
    },
    {
      title: '지사명',
      align: 'center',
      dataIndex: 'BRANCH_NAME'
    },
    {
      title: '교육원명',
      align: 'center',
      dataIndex: 'SCHOOL_NAME'
    },
    {
      title: '초도구분',
      align: 'center',
      dataIndex: 'FIRST_PRODUCT_APPROVE_STATUS',
      render: (text, record) => {
        return (
          <Space direction="vertical">
            <Space size="small">{firstOrderApproveStatusOptions.find((it) => it.value == text)?.label}</Space>
            {text === 1 && (
              <Space size={3}>
                <FillButton label="승인" color={'var(--color-violet02)'} backgroundColor={'var(--color-violet01)'} onClick={() => fnChangeStatus(record, { first_product_approve_status: 3, order_status: 3 })} />
                <FillButton label="반려" color={'var(--color-orange04)'} backgroundColor={'var(--color-orange03)'} onClick={() => fnChangeStatus(record, { first_product_approve_status: 2, order_status: 2 })} />
              </Space>
            )}
          </Space>
        );
      }
    },
    {
      title: '주문일',
      align: 'center',
      dataIndex: 'ORDER_DATE'
    },
    {
      title: '주문상태',
      align: 'center',
      dataIndex: 'ORDER_STATUS',
      render: (text) => {
        const options = firstOrderStatusOptions;
        return options.find((it) => it.value == text)?.label;
      }
    },
    {
      title: '승인반려',
      align: 'center',
      dataIndex: 'IDX'
    }
  ];

  const rowSelection = {
    type: 'checkbox',
    selectedRowKeys: rows,
    onChange: (selectedRowKey, selectedRows) => {
      setRows(selectedRowKey);
    }
  };

  return (
    <>
      <BaseSection>
        <SearchDesc
          inline
          // stats={stats}
          items={items}
          column={items.length}
          labelStyle={{ width: '50px' }}
        />
        <SearchDesc inline items={items2} column={items2.length} labelStyle={{ width: '50px' }} />

        <BaseContainer>
          <Row>
            <Col span={24}>
              <Space>
                <GridSetting
                  data={{
                    defaultCheckedList: [],
                    options: []
                  }}
                  onChange={() => {}}
                />
                <DownloadExcel label="엑셀 다운로드(검색)" params={''} />
                <DownloadExcel label="엑셀 다운로드(체크박스)" params={''} />
                <DownloadExcel label="엑셀 PDF 배송용 출력" params={''} />
                <IconFilePdf />
                PDF 배송용 출력
                {/* <IconPrinter />PDF 배송용 출력 */}
              </Space>
              <Divider className="divider-small" />
              <DataGrid
                rowKey={'ORDER_IDX'}
                columns={columns}
                data={dataList}
                pagination
                rowSelection={{
                  ...rowSelection
                }}
              />
            </Col>
          </Row>
        </BaseContainer>
      </BaseSection>

      {/* =============모달영역===================== */}
      <AlertModal open={alertModal?.open} onCancel={fnCloseAlertModal}>
        {alertModal?.message}
      </AlertModal>
    </>
  );
};
export default InitialProductOrder;
